import { create, type StateCreator } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { formatMinorUnits } from '@/utils/format-amount';

const IS_DEV = import.meta.env['DEV'];

/**
 * Game phases in the crash game lifecycle
 */
export type GamePhase = 'waiting' | 'betting' | 'playing' | 'crashed';

/**
 * Player bet state
 */
export interface Bet {
  amount: number;
  autoCashout: number | null;
  cashedOut: boolean;
  cashoutMultiplier: number | null;
}

/**
 * Connection status for more detailed state
 */
export type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error';

/**
 * Game state interface
 */
export interface GameState {
  // Connection
  isConnected: boolean;
  connectionStatus: ConnectionStatus;
  connectionError: string | null;
  sessionId: string | null;

  // Game state
  gamePhase: GamePhase;
  multiplier: number;
  crashMultiplier: number | null;
  roundId: string | null;
  seedHash: string | null; // Provably fair commitment

  // Player state
  balance: number;
  currentBet: Bet | null;

  /** R22-3: currency code (ISO-4217 fiat or crypto symbol). */
  currency: string;

  /**
   * R22-3: currency fraction-digit precision. Supplied by the launcher via
   * `currencyDecimals` URL param (mirror of `ShellConfig.currencyDecimals`
   * from game-shell-contract 0.6.0). Null means "derive from currency
   * registry at format time".
   */
  currencyDecimals: number | null;

  // History
  crashHistory: number[];

  // Stream recovery
  lastSequenceId: number;
}

/**
 * Game actions interface
 */
export interface GameActions {
  // Connection actions
  setConnected: (connected: boolean) => void;
  setConnectionStatus: (status: ConnectionStatus) => void;
  setConnectionError: (error: string | null) => void;
  setSessionId: (sessionId: string) => void;

  // Game actions
  setGamePhase: (phase: GamePhase) => void;
  setMultiplier: (multiplier: number) => void;
  setCrash: (crashMultiplier: number) => void;
  setRoundId: (roundId: string) => void;
  setSeedHash: (seedHash: string) => void;

  // Player actions
  setBalance: (balance: number) => void;
  placeBet: (amount: number, autoCashout?: number) => void;
  cashOut: (cashoutMultiplier?: number, winAmount?: number) => void;
  clearBet: () => void;
  /** R22-3: currency actions */
  setCurrency: (currency: string) => void;
  setCurrencyDecimals: (decimals: number | null) => void;

  // History
  addCrashToHistory: (multiplier: number) => void;

  // Stream recovery
  setLastSequenceId: (sequenceId: number) => void;

  // Reset
  reset: () => void;
}

const initialState: GameState = {
  isConnected: false,
  connectionStatus: 'disconnected',
  connectionError: null,
  sessionId: null,
  gamePhase: 'waiting',
  multiplier: 1.0,
  crashMultiplier: null,
  roundId: null,
  seedHash: null,
  balance: 0,
  currentBet: null,
  currency: 'EUR',
  currencyDecimals: null,
  crashHistory: [],
  lastSequenceId: 0,
};

/**
 * Debug logging middleware for development
 */
const debugLogger =
  <T extends GameState & GameActions>(
    config: StateCreator<T>
  ): StateCreator<T> =>
  (set, get, api) =>
    config(
      (args) => {
        if (IS_DEV) {
          const prevState = get();
          set(args);
          const nextState = get();

          // Find changed keys
          const changes: Record<string, { from: unknown; to: unknown }> = {};
          for (const key of Object.keys(nextState) as Array<keyof T>) {
            if (typeof nextState[key] !== 'function' && prevState[key] !== nextState[key]) {
              changes[key as string] = { from: prevState[key], to: nextState[key] };
            }
          }

          if (Object.keys(changes).length > 0) {
            console.groupCollapsed(
              `%c[GameStore]`,
              'color: #dc2626; font-weight: bold',
              Object.keys(changes).join(', ')
            );
            console.log('Changes:', changes);
            console.log('Full state:', nextState);
            console.groupEnd();
          }
        } else {
          set(args);
        }
      },
      get,
      api
    );

/**
 * Main game store using Zustand
 * Manages all game state including connection, betting, and crash history
 */
export const useGameStore = create<GameState & GameActions>()(
  devtools(
    subscribeWithSelector(
      debugLogger((set) => ({
        ...initialState,

        // Connection actions
        setConnected: (connected: boolean): void => {
          set((state) => ({
            isConnected: connected,
            connectionStatus: connected ? 'connected' : 'disconnected',
            connectionError: connected ? null : state.connectionError,
          }));
        },
        setConnectionStatus: (status: ConnectionStatus): void => {
          set({ connectionStatus: status });
        },
        setConnectionError: (error: string | null): void => {
          set((state) => ({
            connectionError: error,
            connectionStatus: error ? 'error' : state.connectionStatus,
          }));
        },
        setSessionId: (sessionId: string): void => {
          set({ sessionId });
        },

        // Game actions
        setGamePhase: (phase: GamePhase): void => {
          set((state) => ({
            gamePhase: phase,
            // Reset multiplier when entering betting phase
            multiplier: phase === 'betting' ? 1.0 : state.multiplier,
            crashMultiplier: phase === 'betting' ? null : state.crashMultiplier,
          }));
        },

        setMultiplier: (multiplier: number): void => {
          set({ multiplier });
        },

        setCrash: (crashMultiplier: number): void => {
          set({
            crashMultiplier,
            gamePhase: 'crashed',
          });
        },

        setRoundId: (roundId: string): void => {
          set({ roundId });
        },

        setSeedHash: (seedHash: string): void => {
          set({ seedHash });
        },

        // Player actions
        setBalance: (balance: number): void => {
          set({ balance });
        },

        placeBet: (amount: number, autoCashout?: number): void => {
          set((state) => ({
            currentBet: {
              amount,
              autoCashout: autoCashout ?? null,
              cashedOut: false,
              cashoutMultiplier: null,
            },
            balance: state.balance - amount,
          }));
        },

        cashOut: (cashoutMultiplier?: number, winAmount?: number): void => {
          set((state) => {
            if (!state.currentBet || state.currentBet.cashedOut) return state;

            // Use provided values or calculate from current state
            const finalMultiplier = cashoutMultiplier ?? state.multiplier;
            const finalWinAmount = winAmount ?? state.currentBet.amount * finalMultiplier;

            return {
              currentBet: {
                ...state.currentBet,
                cashedOut: true,
                cashoutMultiplier: finalMultiplier,
              },
              balance: state.balance + finalWinAmount,
            };
          });
        },

        clearBet: (): void => {
          set({ currentBet: null });
        },

        // R22-3 currency actions
        setCurrency: (currency: string): void => {
          set({ currency });
        },

        setCurrencyDecimals: (decimals: number | null): void => {
          set({ currencyDecimals: decimals });
        },

        // History
        addCrashToHistory: (multiplier: number): void => {
          set((state) => ({
            crashHistory: [multiplier, ...state.crashHistory.slice(0, 19)],
          }));
        },

        // Stream recovery
        setLastSequenceId: (sequenceId: number): void => {
          set({ lastSequenceId: sequenceId });
        },

        // Reset
        reset: (): void => {
          set(initialState);
        },
      }))
    ),
    { name: 'aviator-game-store', enabled: IS_DEV }
  )
);

// ============================================================================
// SELECTORS - Derived state computed from store
// ============================================================================

/**
 * Select potential win amount based on current bet and multiplier
 */
export const selectPotentialWin = (state: GameState): number => {
  if (!state.currentBet || state.currentBet.cashedOut) return 0;
  return state.currentBet.amount * state.multiplier;
};

/**
 * Select potential profit (win minus bet)
 */
export const selectPotentialProfit = (state: GameState): number => {
  if (!state.currentBet || state.currentBet.cashedOut) return 0;
  return state.currentBet.amount * (state.multiplier - 1);
};

/**
 * Check if player can place a bet
 */
export const selectCanBet = (state: GameState): boolean => {
  return (
    state.isConnected &&
    state.gamePhase === 'betting' &&
    state.currentBet === null &&
    state.balance > 0
  );
};

/**
 * Check if player can cash out
 */
export const selectCanCashOut = (state: GameState): boolean => {
  return (
    state.isConnected &&
    state.gamePhase === 'playing' &&
    state.currentBet !== null &&
    !state.currentBet.cashedOut
  );
};

/**
 * Check if player has an active (not cashed out) bet
 */
export const selectHasActiveBet = (state: GameState): boolean => {
  return state.currentBet !== null && !state.currentBet.cashedOut;
};

/**
 * Get the last N crash values from history
 */
export const selectRecentCrashes = (count: number) => (state: GameState): number[] => {
  return state.crashHistory.slice(0, count);
};

/**
 * Get average crash multiplier from history
 */
export const selectAverageCrash = (state: GameState): number => {
  if (state.crashHistory.length === 0) return 0;
  const sum = state.crashHistory.reduce((acc, val) => acc + val, 0);
  return sum / state.crashHistory.length;
};

/**
 * Check if currently in a playable state (betting or playing)
 */
export const selectIsPlayable = (state: GameState): boolean => {
  return state.gamePhase === 'betting' || state.gamePhase === 'playing';
};

/**
 * Format balance for display.
 *
 * R22-3: balance is stored in MINOR units. We now divide by 10^decimals
 * (not hardcoded /100) and render with the matching precision so BTC
 * operators see sat -> 8-decimal BTC correctly.
 */
export const selectFormattedBalance = (state: GameState): string => {
  return formatMinorUnits(state.balance, state.currency, state.currencyDecimals ?? undefined);
};

/**
 * Get win amount if player cashed out
 */
export const selectWinAmount = (state: GameState): number | null => {
  if (!state.currentBet || !state.currentBet.cashedOut || !state.currentBet.cashoutMultiplier) {
    return null;
  }
  return state.currentBet.amount * state.currentBet.cashoutMultiplier;
};
